import {useMemo, useState} from "react";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "../../../basic/dialog.tsx";
import {toast} from "sonner";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useErrorHandler} from "../../../../hooks/useErrorHandler";
import {UserRoleSection} from "./UserRoleSection.tsx";
import {LicenseList} from "./LicenseList.tsx";
import {LicenseSelector} from "../../../shared/selectors/LicenseSelector.tsx";
import {DialogActions} from "./DialogActions.tsx";
import {
    useAddLicenseAssignmentMutation,
    useGetPortalLicensesQuery,
    useGetPortalTeamQuery,
    useRemoveLicenseAssignmentMutation,
    useUpdateUserRoleMutation,
    VwpTeamMemberLicense,
    UserRole
} from "../../../../store/generated/api.ts";
import {filterAvailableLicenses} from "../../../../utils/licenseUtils";
import {getDisplayName} from "../../../../utils/nameUtils";

interface ManageLicensesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedMember: {
        id: number;
        name: string;
        email: string;
        role: string;
        licenses: VwpTeamMemberLicense[];
    } | null;
}

export function ManageUsersDialog({
                                      open,
                                      onOpenChange,
                                      selectedMember
                                  }: ManageLicensesDialogProps) {
    const {t} = useTranslationWithKeys();
    const {showErrorToast, handleApiError} = useErrorHandler();
    const [selectedLicenseToAdd, setSelectedLicenseToAdd] = useState("");
    const [pendingRole, setPendingRole] = useState<string | null>(null);
    const [removeLicenseAssignment, {isLoading: isRemovingLicense}] = useRemoveLicenseAssignmentMutation();
    const [addLicenseAssignment, {isLoading: isAddingLicense}] = useAddLicenseAssignmentMutation();
    const [updateUserRole, {isLoading: isUpdatingRole}] = useUpdateUserRoleMutation();

    // Get portal licenses with RTK Query - this will be automatically invalidated when removeLicenseAssignment is called
    const {data: portalLicenses, isLoading: isLoadingLicenses} = useGetPortalLicensesQuery({});

    // Get fresh team data to ensure we have updated license information
    const {data: teamData} = useGetPortalTeamQuery({});

    const handleAddLicense = async () => {
        if (!selectedLicenseToAdd) {
            showErrorToast({message: t('team.selectLicenseToAdd')});
            return;
        }

        if (!selectedMember) {
            showErrorToast({message: t('common.error')});
            return;
        }

        try {
            // Find the license object to get the actual license ID
            const selectedLicenseObject = availableLicensesFiltered.find(license => license.id === selectedLicenseToAdd);
            if (!selectedLicenseObject) {
                showErrorToast({message: t('common.error')});
                return;
            }

            await addLicenseAssignment({
                vwpAddLicenseAssignmentRequest: {
                    userEmail: selectedMember.email,
                    licenseId: selectedLicenseObject.id
                }
            }).unwrap();

            toast.success(t('team.licenseAssigned', {
                license: selectedLicenseObject.product,
                name: getDisplayName(selectedMember)
            }));
            setSelectedLicenseToAdd("");
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('team.licenseAssignError')});
        }
    };

    const handleRemoveLicense = async (license: VwpTeamMemberLicense) => {
        if (!selectedMember) {
            showErrorToast({message: t('common.error')});
            return;
        }

        try {
            await removeLicenseAssignment({
                vwpRemoveLicenseAssignmentRequest: {
                    userEmail: selectedMember.email,
                    licenseId: license.id
                }
            }).unwrap();

            toast.success(t('team.licenseRemoved', {license: license.name, name: getDisplayName(selectedMember)}));
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('team.licenseRemoveError', {name: getDisplayName(selectedMember)})});
        }
    };

    const handleRoleChange = (newRole: string) => {
        setPendingRole(newRole);
    };

    const handleSaveRole = async () => {
        if (!selectedMember || !pendingRole) {
            return;
        }

        try {
            // Map frontend role values to API values
            const apiRole = pendingRole.toUpperCase() as UserRole;

            await updateUserRole({
                email: selectedMember.email,
                vwpUpdateUserRoleRequest: {
                    role: apiRole
                }
            }).unwrap();

            toast.success(t('team.roleUpdated', {
                name: getDisplayName(selectedMember),
                role: t(`common.role.${pendingRole}`)
            }));
            setPendingRole(null);
        } catch (error) {
            handleApiError(error, {fallbackMessage: t('team.roleUpdateError')});
        }
    };

    // Find the updated member data from the fresh team data
    const updatedMember = teamData?.teamMembers?.members?.find(member => member.id === selectedMember?.id);

    // Create enriched license objects that combine portal license data with updated member license assignments
    const memberLicenses = updatedMember?.licenses?.map(memberLicense => {
        const portalLicense = portalLicenses?.licenses?.find(license => license.id === memberLicense.id);
        return {
            ...memberLicense,
            name: portalLicense?.product || 'Unknown License'
        };
    }) || [];

    const availableLicensesFiltered = useMemo(() => {
        if (!portalLicenses?.licenses) return [];

        const availableLicenses = filterAvailableLicenses(portalLicenses.licenses);

        return availableLicenses.filter(license =>
            !memberLicenses?.some(memberLicense => memberLicense.id === license.id)
        );
    }, [portalLicenses, memberLicenses]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('team.manageMember')}</DialogTitle>
                    <DialogDescription className="break-words">
                        {t('team.manageMemberDescription', {name: selectedMember ? getDisplayName(selectedMember) : ''})}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <UserRoleSection
                        member={selectedMember}
                        currentRole={selectedMember?.role.toLowerCase()}
                        pendingRole={pendingRole}
                        onRoleChange={handleRoleChange}
                        isUpdatingRole={isUpdatingRole}
                        isUniversity={teamData?.team?.isUniversity === true}
                    />

                    <div className="space-y-3">
                        <LicenseList
                            licenses={memberLicenses}
                            onRemoveLicense={handleRemoveLicense}
                            isRemovingLicense={isRemovingLicense}
                        />

                        <LicenseSelector
                            value={selectedLicenseToAdd}
                            onValueChange={setSelectedLicenseToAdd}
                            licenses={availableLicensesFiltered}
                            label={t('common.team.addLicense')}
                            placeholder={t('common.team.selectLicenseToAdd')}
                            actionButton={{
                                text: t('common.action.add'),
                                onClick: handleAddLicense,
                                disabled: !selectedLicenseToAdd || isAddingLicense,
                                loading: isAddingLicense
                            }}
                        />
                    </div>
                </div>
                <DialogActions
                    onClose={() => {
                        onOpenChange(false);
                        setPendingRole(null);
                    }}
                    onSave={handleSaveRole}
                    saveDisabled={!pendingRole || isUpdatingRole}
                    saveText={t('common.save')}
                    isSaving={isUpdatingRole}
                />
            </DialogContent>
        </Dialog>
    );
}